import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, apiDownload } from '../api/client.js';
import type { Attachment, WorkOrderDetail } from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS } from '../../shared/permissions.js';
import { AttachmentUploader } from '../components/AttachmentUploader.js';
import { Button, Card, EmptyState, InternalBadge, Spinner } from '../components/ui.js';
import { ConfirmDialog } from '../components/Modal.js';
import { formatBytes, formatDateTime, humanizeAction } from '../lib/format.js';

export function AttachmentsSection({ workOrder }: { workOrder: WorkOrderDetail }) {
  const queryClient = useQueryClient();
  const { can, user } = useSession();
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState<Attachment | null>(null);

  const mayUpload = can(PERMISSIONS.ATTACHMENT_UPLOAD) && workOrder.status !== 'closed';

  const { data, isLoading } = useQuery({
    queryKey: ['attachments', workOrder.id],
    queryFn: () =>
      apiRequest<{ attachments: Attachment[] }>(
        `/api/work-orders/${workOrder.id}/attachments`,
      ).then((result) => result.attachments),
  });

  const remove = useMutation({
    mutationFn: (attachmentId: string) =>
      apiRequest(`/api/attachments/${attachmentId}`, { method: 'DELETE' }),
    onSuccess: () => {
      setRemoving(null);
      void queryClient.invalidateQueries({ queryKey: ['attachments', workOrder.id] });
    },
  });

  return (
    <Card
      title="Documents and photos"
      actions={
        mayUpload && (
          <Button variant="secondary" onClick={() => setUploading((previous) => !previous)}>
            {uploading ? 'Hide upload' : 'Upload files'}
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {uploading && mayUpload && (
          <div className="rounded-md bg-slate-50 p-3">
            <AttachmentUploader workOrderId={workOrder.id} />
          </div>
        )}

        {isLoading ? (
          <Spinner label="Loading documents" />
        ) : (data ?? []).length === 0 ? (
          <EmptyState
            title="No documents yet"
            description="Photos of the problem, quotes, and completion photos appear here."
          />
        ) : (
          <ul className="divide-y divide-slate-200">
            {(data ?? []).map((attachment) => (
              <li key={attachment.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        void apiDownload(
                          `/api/attachments/${attachment.id}/content`,
                          attachment.fileName,
                        )
                      }
                      className="truncate text-sm font-medium text-brand-800 hover:underline"
                    >
                      {attachment.fileName}
                    </button>
                    {attachment.visibility === 'internal' && <InternalBadge />}
                  </div>
                  <p className="text-xs text-slate-600">
                    {humanizeAction(attachment.category)} · {formatBytes(attachment.byteSize)} ·{' '}
                    {attachment.uploadedByName} · {formatDateTime(attachment.uploadedAt)}
                  </p>
                  {attachment.description && (
                    <p className="mt-0.5 text-sm text-slate-700">{attachment.description}</p>
                  )}
                </div>
                {attachment.uploadedByUserId === user?.userId && workOrder.status !== 'closed' && (
                  <Button variant="ghost" onClick={() => setRemoving(attachment)}>
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={removing !== null}
        title="Remove this file?"
        message={
          removing
            ? `${removing.fileName} will no longer be listed. The record is retained for audit purposes.`
            : ''
        }
        confirmLabel="Remove"
        variant="danger"
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (removing) remove.mutate(removing.id);
        }}
      />
    </Card>
  );
}
